/** ListHeaderCell — header cell with reorder grip (HTML5 DnD), sort label, and a pointer-drag resize handle. */
import React, { useRef } from 'react'
import TableCell from '@mui/material/TableCell'
import TableSortLabel from '@mui/material/TableSortLabel'
import Box from '@mui/material/Box'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import { widthSx } from './widthSx'
import type { ListTableColumn } from './types'
import { monoSx } from '@/shared/listCell'
import { listCellHairlineSx, listHeaderStickySx } from './listTableChrome'

export const MIN_COL_WIDTH = 60

export interface ListHeaderCellProps {
  col: ListTableColumn
  sortActive: boolean
  sortDir: 'asc' | 'desc'
  onSort: () => void
  onResize: (name: string, px: number) => void
  onResetWidth: (name: string) => void
  onReorder: (fromName: string, toName: string) => void
}

export const ListHeaderCell: React.FC<ListHeaderCellProps> = ({ col, sortActive, sortDir, onSort, onResize, onResetWidth, onReorder }) => {
  const start = useRef<{ x: number; w: number } | null>(null)

  const onPointerDown = (e: React.PointerEvent) => {
    e.preventDefault(); e.stopPropagation()
    const th = (e.currentTarget as HTMLElement).closest('th') as HTMLElement | null
    start.current = { x: e.clientX, w: th?.getBoundingClientRect().width ?? MIN_COL_WIDTH }
    const move = (ev: PointerEvent) => { if (start.current) onResize(col.name, Math.max(MIN_COL_WIDTH, start.current.w + (ev.clientX - start.current.x))) }
    const up = () => { start.current = null; window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up) }
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up)
  }

  return (
    <TableCell
      data-testid={`col-header-${col.name}`}
      sx={{
        ...widthSx(col.width),
        ...(col.mono ? monoSx : {}),
        ...listHeaderStickySx,
        ...listCellHairlineSx,
        '&:hover .col-grip': { opacity: 0.5 },
      }}
      draggable
      onDragStart={(e) => e.dataTransfer.setData('text/plain', col.name)}
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => { e.preventDefault(); const from = e.dataTransfer.getData('text/plain'); if (from && from !== col.name) onReorder(from, col.name) }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <DragIndicatorIcon className="col-grip" sx={{ fontSize: 14, opacity: 0, cursor: 'grab' }} />
        <TableSortLabel active={sortActive} direction={sortActive ? sortDir : 'asc'} onClick={onSort}>
          {col.title || col.name}
        </TableSortLabel>
      </Box>
      <Box
        data-testid={`col-resize-${col.name}`}
        onPointerDown={onPointerDown}
        onDoubleClick={(e) => { e.stopPropagation(); onResetWidth(col.name) }}
        sx={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 6, cursor: 'col-resize', userSelect: 'none' }}
      />
    </TableCell>
  )
}
